import { t } from "i18next";
//@ts-ignore
import Pagination from "react-js-pagination";
import TinyLoader from "../../../loaders/TinyLoader";
import SearchField from "../../../common/SearchField";
import UserProfileHoverCard from "../../../common/UserProfileHoverCard";
import { AnalyticsWidgetUiDetailsDto } from "../../../../types/analytics/AnalyticsWidgetUiDetailsDto";
import AnalyticsFilterInputs from "../../../../types/analytics/AnalyticsFilterInputs";
import MultiScoreTableMobile from "./MultiScoreTableMobile";
import { useMultiScoreTable } from "./useMultiScoreTable";
import { displayEmployeeDimensionLabel } from "../../../../helpers/analytics/displayEmployeeDimensionLabel";

interface MultiScoreTableProps {
  widget: AnalyticsWidgetUiDetailsDto;
  analyticsPageId: number;
  filters: AnalyticsFilterInputs;
}

const MultiScoreTable = ({
  widget,
  analyticsPageId,
  filters,
}: MultiScoreTableProps) => {
  const {
    dataToRender,
    isLoading,
    noResultsLoaded,
    pageSize,
    totalItemCount,
    pageSearchDetails,
    handlePageChange,
    handleSearchChange,
    userContext,
  } = useMultiScoreTable({ widget, analyticsPageId, filters });

  const isSnapshot =
    (filters.pointInTimeDate?.trim() ?? "") !== "";

  if (!dataToRender) return null;

  return (
    <div className="w-full">
      {/* Mobile Version */}
      <div className="md:hidden">
        <MultiScoreTableMobile
          widget={widget}
          analyticsPageId={analyticsPageId}
          filters={filters}
        />
      </div>

      {/* Desktop Version */}
      <div className="hidden md:block">
        <SearchField
          onSearch={handleSearchChange}
          isLoading={isLoading}
          loaderColor={userContext.user.client.accentHexColour}
        />

        {noResultsLoaded && (
          <em className="block text-center italic py-4">
            {t("Pages.Analytics.Common.NoTableData")}
          </em>
        )}

        {!noResultsLoaded && (
          <>
            <div className="relative border rounded-lg mt-2 overflow-hidden">
              <div className="grid grid-cols-[auto,1fr]">
                {/* Fixed Columns */}
                <div className="sticky left-0 z-20 bg-white">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="h-11 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[300px] min-w-[16rem] border-r">
                          Employee
                        </th>
                        <th className="h-11 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px] border-r">
                          Level
                        </th>
                        <th className="h-11 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px] border-r">
                          Location
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dataToRender.map((employee) => (
                        <tr key={employee.id}>
                          <td className="h-[72px] px-6 w-[300px] min-w-[16rem] border-r align-middle">
                            <div className="flex flex-col justify-center h-full">
                              <div className="text-sm text-gray-500">
                                <UserProfileHoverCard employeeId={employee.id}>
                                  {employee.fullName}
                                </UserProfileHoverCard>
                              </div>
                              <div className="text-sm text-gray-500">
                                {displayEmployeeDimensionLabel(
                                  employee.jobTitle,
                                  isSnapshot,
                                  t,
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="h-[72px] px-6 text-sm text-gray-500 w-[100px] border-r align-middle">
                            {displayEmployeeDimensionLabel(
                              employee.level,
                              isSnapshot,
                              t,
                            )}
                          </td>
                          <td className="h-[72px] px-6 text-sm text-gray-500 w-[200px] border-r align-middle">
                            {employee.location.join(" > ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Scrollable Scores */}
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {dataToRender[0]?.scores.map((score) => (
                          <th
                            key={score.id}
                            className="h-11 px-6 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px] whitespace-nowrap border-r last:border-r-0"
                          >
                            {score.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dataToRender.map((employee) => (
                        <tr key={employee.id}>
                          {employee.scores.map((score) => (
                            <td
                              key={score.id}
                              className="h-[72px] px-6 text-sm text-gray-900 text-center w-[150px] whitespace-nowrap border-r last:border-r-0 align-middle"
                              style={{
                                color: score.hexColour
                                  ? score.hexColour
                                  : undefined,
                              }}
                            >
                              {score.value === null ? "-" : score.value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {totalItemCount > pageSize && (
              <div className="flex flex-row mb-4 mt-4">
                <div>
                  <Pagination
                    activePage={pageSearchDetails.pageNumber}
                    itemsCountPerPage={pageSize}
                    totalItemsCount={totalItemCount}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    activeClass="pagination-active-list-item"
                    itemClass="pagination-list-item"
                    itemClassFirst="pagination-first-item"
                    itemClassLast="pagination-last-item"
                  />
                </div>
                {isLoading && (
                  <div className="pl-4">
                    <TinyLoader
                      colour={userContext.user.client.accentHexColour}
                    />
                    <span className="ml-1 text-gray-500 text-sm">
                      {t("Common.Loading")}...
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MultiScoreTable;
